import { useCallback } from 'react';

import { Box, IconButton, Tooltip } from '@mui/material';

import MapPinIcon from '@/components/Icons/MapPinIcon';
import MapPinnedIcon from '@/components/Icons/MapPinnedIcon';
import { MAX_PINNED_IDS } from '@/constants';
import useIsNewTrainingUIEnabled from '@/hooks/useIsNewTrainingUIEnabled';
import { usePromiseSnackbar } from '@/hooks/usePromiseSnackbar';
import useUser from '@/hooks/useUser';
import useSafeguardConfirmationDialog from '@/sections/Dialog/SafeguardConfirmation';
import { POLL_INTERVAL } from '@/sections/Workspace/hooks';
import { useSnackbar } from 'notistack';
import { useRecoilState } from 'recoil';

import { pinnedIdsState } from '../atoms';
import { WorkspaceSections } from '../types';
import { sortPinnedIds } from '../utils';
import { useDeleteTrainingProjectMutation } from './__generated__/mutation.generated';
import {
  TrainingProjectsDocument,
  useTrainingProjectsQuery,
} from './__generated__/queries.generated';

function useTrainingProjects(poll = false) {
  const isNewTrainingUIEnabled = useIsNewTrainingUIEnabled();
  const { data, loading } = useTrainingProjectsQuery({
    fetchPolicy: 'cache-first',
    pollInterval: poll ? POLL_INTERVAL : null,
    skip: !isNewTrainingUIEnabled,
  });

  return {
    trainingProjects: data?.trainingProjects || [],
    loading,
  };
}

function usePinnedAPI() {
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [pinnedIds, setPinnedIds] = useRecoilState(
    pinnedIdsState(`${WorkspaceSections.Training}-${user.id}`),
  );
  const { trainingProjects, loading } = useTrainingProjects();

  const pinnedTrainingProjects = trainingProjects
    .filter((trainingProject) => pinnedIds.includes(trainingProject.id))
    .sort((a, b) => sortPinnedIds(pinnedIds, a.id, b.id));

  const togglePinEntity = useCallback(
    (entityId: string, entityName: string) => {
      setPinnedIds((prev) => {
        if (prev.includes(entityId)) {
          return prev.filter((id) => id !== entityId);
        }
        enqueueSnackbar(`Pinned ${entityName} to the sidebar`, { variant: 'success' });
        return [entityId, ...prev];
      });
    },
    [setPinnedIds, enqueueSnackbar],
  );

  const canPinMoreTrainingProjects = pinnedTrainingProjects.length < MAX_PINNED_IDS;

  function stopPropagation(ev: React.MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  function getPinActionItem(entityId: string, entityName: string) {
    const isPinned = pinnedIds.includes(entityId);
    const isPinDisabled = !canPinMoreTrainingProjects && !isPinned;

    return (
      <Tooltip
        title={
          isPinDisabled
            ? `Limit ${MAX_PINNED_IDS} pins`
            : isPinned
            ? 'Unpin from sidebar'
            : 'Pin to sidebar'
        }
      >
        <Box>
          <IconButton
            onClick={(e) => {
              stopPropagation(e);
              togglePinEntity(entityId, entityName);
            }}
            data-cy="pin-entity-btn"
            disabled={isPinDisabled}
          >
            {isPinned ? <MapPinnedIcon color="inherit" /> : <MapPinIcon color="secondary" />}
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return {
    pinnedIds,
    togglePinEntity,
    canPinMoreTrainingProjects,
    getPinActionItem,
    pinnedTrainingProjects,
    loading,
  };
}

function useDeleteTrainingProject(id: string, name: string) {
  const promiseSnackbar = usePromiseSnackbar();
  const [deleteProject] = useDeleteTrainingProjectMutation();
  const safeguardConfirmationDialog = useSafeguardConfirmationDialog();

  return useCallback(async () => {
    const confirmed = await safeguardConfirmationDialog({
      title: 'Delete Training Project',
      okText: 'Delete',
      prompt: 'Are you sure you want to delete this training project? This cannot be undone.',
      validationText: name,
      validationHint: 'Training project name',
    });

    if (confirmed) {
      promiseSnackbar(
        deleteProject({
          variables: { id },
          refetchQueries: [TrainingProjectsDocument],
        }),
      );
    }
  }, [id, name, deleteProject, safeguardConfirmationDialog, promiseSnackbar]);
}

export { useTrainingProjects, usePinnedAPI, useDeleteTrainingProject };
